/**
 * MasterDataShippingDocFormDialog — Create/Edit `shipping_doc`.
 * 1:1 port of Access `frmMdQryShippingDocAdd`. Compact Sales Contract style.
 *
 * Single-column, field order by Top:
 *   Shipping Doc Code → Shipping Doc → Note.
 * `shipping_doc_code` là PK auto-suggest — áp /access-suggest:
 *   - New: auto-suggest khi mở + blur-check trùng → tự đổi mã + alert vàng.
 *   - Edit: disable, không suggest/check.
 * VBA errCheck: code trống → "Please enter valid Shipping Doc Code!"; shipping_doc
 *   trống → "Please type Shipping Doc!"; code trùng (create) → "Shipping Doc Code
 *   existed! Please enter a new Code!" (BE raise).
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdShippingDocMutation,
  useLazyCheckMdShippingDocCodeQuery,
  useLazySuggestMdShippingDocCodeQuery,
  useUpdateMdShippingDocMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataShippingDoc } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  roSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataShippingDoc | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  shipping_doc_code: string
  shipping_doc: string
  note: string
}

const EMPTY: FormState = { shipping_doc_code: '', shipping_doc: '', note: '' }

export default function MasterDataShippingDocFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createShippingDoc, { isLoading: creating }] = useCreateMdShippingDocMutation()
  const [updateShippingDoc, { isLoading: updating }] = useUpdateMdShippingDocMutation()
  const [triggerSuggest, suggestState] = useLazySuggestMdShippingDocCodeQuery()
  const [triggerCheck] = useLazyCheckMdShippingDocCodeQuery()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  // Hydrate — chỉ depend [open, mode, initialValue] (access-suggest anti #4).
  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      setForm({
        shipping_doc_code: initialValue.shipping_doc_code ?? '',
        shipping_doc: initialValue.shipping_doc ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  // New: auto-suggest shipping_doc_code khi mở (lazy, guard chỉ set khi trống).
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const newVal = (res.shipping_doc_code || '').trim()
      if (!newVal) return
      setForm((prev) => (prev.shipping_doc_code.trim() ? prev : { ...prev, shipping_doc_code: newVal }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  // Dismiss alert khi user sửa code khác mã suggest.
  useEffect(() => {
    if (regenNotice && form.shipping_doc_code !== regenNotice.to) setRegenNotice(null)
  }, [form.shipping_doc_code, regenNotice])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.shipping_doc_code.trim()
  const shippingDocEmpty = !form.shipping_doc.trim()
  const canSave = !codeEmpty && !shippingDocEmpty && !loading

  // Blur field PK → check trùng, nếu có thì suggest mã mới (access-suggest).
  const handleCodeBlur = async () => {
    if (isEdit) return
    const val = form.shipping_doc_code.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(val, false).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const newVal = (next.shipping_doc_code || '').trim()
      if (newVal && newVal !== val) {
        setForm((prev) => ({ ...prev, shipping_doc_code: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      /* BE raise lúc Save nếu thật sự trùng — không poison UX */
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard (blur + click Save cùng tick)
    if (codeEmpty || shippingDocEmpty) {
      setShowErrors(true)
      return
    }
    const body = {
      shipping_doc: form.shipping_doc.trim(),
      note: form.note.trim() || null,
    }
    try {
      if (isEdit && initialValue) {
        await updateShippingDoc({ code: initialValue.shipping_doc_code, body }).unwrap()
        snack.success('Shipping doc updated')
      } else {
        await createShippingDoc({ shipping_doc_code: form.shipping_doc_code.trim(), ...body }).unwrap()
        snack.success('Shipping doc created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      // access-suggest anti #13: duplicate race → auto re-suggest + alert vàng.
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      if (!isEdit && (lower.includes('exist') || lower.includes('duplicate'))) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const newVal = (next.shipping_doc_code || '').trim()
          const oldVal = form.shipping_doc_code.trim()
          if (newVal && newVal !== oldVal) {
            setForm((prev) => ({ ...prev, shipping_doc_code: newVal }))
            setRegenNotice({ from: oldVal, to: newVal })
            return
          }
        } catch {
          /* fallthrough → snack error */
        }
      }
      snack.error(msg)
    }
  }

  const codeHelper = isEdit
    ? 'Shipping Doc Code cannot be changed after creation.'
    : precheckBusy
      ? 'Verifying code…'
      : suggestState.isFetching
        ? 'Suggesting next code…'
        : 'Suggested code below; edit if needed.'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Shipping Doc' : 'New Shipping Doc'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.shipping_doc_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Shipping Doc Code <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Shipping Doc Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.shipping_doc_code}
              onChange={(e) => set('shipping_doc_code', e.target.value)}
              onBlur={() => void handleCodeBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={codeHelper}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Shipping Doc"
              size="small"
              fullWidth
              required
              error={showErrors && shippingDocEmpty}
              value={form.shipping_doc}
              onChange={(e) => set('shipping_doc', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
